import { v4 as uuidv4 } from 'uuid';
import { getCurrentConference } from '../base/conference/functions';
import { participantJoined, participantLeft } from '../base/participants/actions';
import { getLocalParticipant } from '../base/participants/functions';
import { FakeParticipant } from '../base/participants/types';
import { CLEAR_SGI_SESSION, SET_SGI_SESSION, SET_SGI_STATE, SGI_READY } from './actionTypes';
import { SGI_COMMAND, SGI_DEBUG, SGI_PARTICIPANT_ID } from './constants';
import { getSgiSession, isLocalParticipantSGIModerator } from './functions';
/**
 * Action to set SGI session as ready (iframe loaded).
 *
 * @param {boolean} ready - Whether the iframe signaled it is ready.
 * @returns {Object}
 */
export function setSgiReady(ready = true) {
    return {
        type: SGI_READY,
        ready
    };
}
/**
 * Action to update SGI shared state.
 *
 * @param {Partial<ISGISharedState>} sharedState - The state to store.
 * @returns {Object}
 */
export function setSgiState(sharedState) {
    return {
        type: SET_SGI_STATE,
        sharedState
    };
}
/**
 * Action to clear SGI session.
 *
 * @returns {Object}
 */
export function clearSgiSession() {
    return {
        type: CLEAR_SGI_SESSION
    };
}
/**
 * Start a new SGI session (moderator only)
 * Creates a fake participant for display and broadcasts to all participants.
 *
 * @returns {Function}
 */
export function startSGISession() {
    return (dispatch, getState) => {
        const state = getState();
        const localParticipant = getLocalParticipant(state);
        const conference = getCurrentConference(state);
        if (!localParticipant || !conference) {
            console.error('[SGI] Cannot start session: no local participant or conference');
            return;
        }
        // Check if there's already an active session
        const existingSession = getSgiSession(state);
        if (existingSession?.active) {
            console.warn('[SGI] Cannot start: session already active by', existingSession.moderatorName);
            return;
        }
        const sessionId = uuidv4();
        const sessionData = {
            sessionId,
            moderatorId: localParticipant.id,
            moderatorName: localParticipant.name || 'Moderator',
            startedAt: Date.now()
        };
        // Dispatch local state update
        dispatch({
            type: SET_SGI_SESSION,
            ...sessionData
        });
        // Create fake participant for SGI display
        // Use moderator's name to ensure correct avatar initials and label
        dispatch(participantJoined({
            conference,
            fakeParticipant: FakeParticipant.SharedVideo,
            id: SGI_PARTICIPANT_ID,
            name: localParticipant.name || 'SGI Moderator'
        }));
        // Prepare command data for broadcast
        const commandData = {
            status: 'start',
            sessionId,
            moderatorId: localParticipant.id,
            moderatorName: localParticipant.name || 'Moderator',
            timestamp: Date.now()
        };
        // Broadcast strategies:
        // 1. Iterative Unicast (Reliable): Send to each participant individually
        // 2. Broadcast (Fallback): Send to empty ID
        broadcastSgiMessage(conference, state, commandData);
        if (SGI_DEBUG) {
            console.log('[SGI] Session started:', sessionId);
        }
    };
}
/**
 * End the current SGI session.
 *
 * @param {string} reason - Why the session is ending.
 * @returns {Function}
 */
export function endSGISession(reason = 'manual') {
    return (dispatch, getState) => {
        const state = getState();
        const session = getSgiSession(state);
        const conference = getCurrentConference(state);
        const localParticipant = getLocalParticipant(state);
        if (!session) {
            console.warn('[SGI] No active session to end');
            return;
        }
        // Only moderator can end the session manually
        if (reason === 'manual' && session.moderatorId !== localParticipant?.id) {
            console.warn('[SGI] Only moderator can end the session');
            return;
        }
        // Remove fake participant
        dispatch(participantLeft(SGI_PARTICIPANT_ID, conference, {
            fakeParticipant: FakeParticipant.SharedVideo
        }));
        // Clear local state
        dispatch(clearSgiSession());
        // Broadcast end to all participants
        if (conference && localParticipant?.id === session.moderatorId) {
            const commandData = {
                status: 'stop',
                sessionId: session.sessionId,
                moderatorId: session.moderatorId,
                moderatorName: session.moderatorName,
                timestamp: Date.now()
            };
            broadcastSgiMessage(conference, state, commandData);
        }
        if (SGI_DEBUG) {
            console.log('[SGI] Session ended:', session.sessionId, 'Reason:', reason);
        }
    };
}
/**
 * Update and broadcast SGI state (moderator only).
 *
 * @param {Partial<ISGISharedState>} sharedState - The state to apply and broadcast.
 * @returns {Function}
 */
export function updateSGIState(sharedState) {
    return (dispatch, getState) => {
        const state = getState();
        const session = getSgiSession(state);
        const conference = getCurrentConference(state);
        if (!session || !isLocalParticipantSGIModerator(state)) {
            return;
        }
        // Update local state
        const currentState = state['features/sgi'].sharedState;
        const newState = {
            ...currentState,
            ...sharedState,
            version: (currentState?.version ?? 0) + 1
        };
        dispatch(setSgiState(newState));
        // Broadcast to all participants
        if (conference) {
            const commandData = {
                status: 'update',
                sessionId: session.sessionId,
                moderatorId: session.moderatorId,
                moderatorName: session.moderatorName,
                state: newState,
                timestamp: Date.now()
            };
            broadcastSgiMessage(conference, state, commandData);
        }
    };
}
/**
 * Toggle SGI session on/off
 * - If no session: starts new session (moderator only)
 * - If active session owned by local user: ends session
 * - If active session owned by another: does nothing.
 *
 * @returns {Function}
 */
export function toggleSGI() {
    return (dispatch, getState) => {
        const state = getState();
        const session = getSgiSession(state);
        const localParticipant = getLocalParticipant(state);
        if (session?.active) {
            if (session.moderatorId === localParticipant?.id) {
                dispatch(endSGISession('manual'));
            }
        }
        else {
            // Permission is controlled by toolbarButtons config from ASPX
            // If user sees the SGI button, they can start a session
            // First to start becomes the session owner (isModerator:true)
            if (SGI_DEBUG) {
                console.log('[SGI DEBUG] No active session, starting new session...');
            }
            dispatch(startSGISession());
        }
    };
}
/**
 * Helper to broadcast SGI message via Iterative Unicast + Broadcast
 * This ensures delivery even if server-side broadcast is restricted.
 *
 * @param {Object} conference - The current conference.
 * @param {Object} state - The redux state.
 * @param {ISGICommandData} data - The command payload to deliver.
 * @returns {void}
 */
function broadcastSgiMessage(conference, state, data) {
    if (!conference) {
        return;
    }
    const payload = {
        name: SGI_COMMAND,
        value: data.sessionId,
        attributes: data
    };
    // 1. Iterative Unicast (Guaranteed)
    const remoteParticipants = state['features/base/participants'].remote;
    let sentCount = 0;
    if (remoteParticipants) {
        // Filter out fake participants (like sgi-share)
        remoteParticipants.forEach((_, id) => {
            // Skip fake participant IDs
            if (id === SGI_PARTICIPANT_ID || id.startsWith('fake-')) {
                return;
            }
            if (SGI_DEBUG) {
                console.log(`[SGI DEBUG] Sending UNICAST to ${id}`);
            }
            conference.sendEndpointMessage(id, payload);
            sentCount++;
        });
    }
    if (SGI_DEBUG) {
        console.log(`[SGI DEBUG] Broadcasting ${data.status}: sent to ${sentCount} real participants`);
    }
    // 2. Broadcast (Fallback/Future-proof)
    conference.sendEndpointMessage('', payload);
}
